// Re-export Hit component variants
export { HitCard } from './HitCard';
export { HitCompactV2 } from './HitCompactV2';
export type { HitProps } from './types';

// Main Hit component that routes to the appropriate variant
import React, { memo } from 'react';
import { HitCard } from './HitCard';
import { HitCompactV2 } from './HitCompactV2';
import type { HitProps } from './types';
import { useRenderLogger, PerformanceProfiler } from '../../utils/shared/performance-profiler';

/**
 * Hit component - routes to card or compact variant based on props
 */
export const Hit: React.FC<HitProps> = memo(({ hit, sendEvent, variant = 'card', index }) => {
  // Detailed profiling for Hit component
  useRenderLogger(`Hit-${variant}-${hit.objectID || hit.id}`, {
    variant,
    hitId: hit.objectID || hit.id,
    hitReference: hit,
    sendEventReference: sendEvent,
    hitTitle: hit.title,
    hitCompany: hit.company,
  });
  
  // Track previous props to detect what's changing
  // Initialize with current props to avoid false positives on first render
  const isFirstRenderRef = React.useRef(true);
  const prevPropsRef = React.useRef<{ hit?: any; sendEvent?: any; variant?: 'card' | 'compact' }>({
    hit,
    sendEvent,
    variant,
  });
  
  React.useEffect(() => {
    // Skip logging on first render to avoid false positives
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevPropsRef.current = { hit, sendEvent, variant };
      return;
    }
    
    const prev = prevPropsRef.current;
    const changes: string[] = [];
    
    if (prev.hit !== hit) {
      changes.push(`hit ref changed (id: ${hit.objectID || hit.id})`);
    }
    if (prev.sendEvent !== sendEvent) {
      changes.push('sendEvent ref changed');
    }
    // Only log variant change if it's actually different and both are defined
    if (prev.variant !== variant && prev.variant !== undefined && variant !== undefined) {
      changes.push(`variant changed (${prev.variant} -> ${variant})`);
    }
    
    
    prevPropsRef.current = { hit, sendEvent, variant };
  });
  
  if (variant === 'compact') {
    return (
      <PerformanceProfiler id={`HitCompactV2-${hit.objectID || hit.id}`}>
        <HitCompactV2 hit={hit} sendEvent={sendEvent} index={index} />
      </PerformanceProfiler>
    );
  }
  return (
    <PerformanceProfiler id={`HitCard-${hit.objectID || hit.id}`}>
      <HitCard hit={hit} sendEvent={sendEvent} index={index} />
    </PerformanceProfiler>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo - check all relevant props
  const variantEqual = prevProps.variant === nextProps.variant;
  const sendEventEqual = prevProps.sendEvent === nextProps.sendEvent;
  const hitRefEqual = prevProps.hit === nextProps.hit;
  
  // If hit reference is the same, we can skip re-render (most important check)
  if (hitRefEqual && variantEqual && sendEventEqual) {
    return true; // All props are the same, skip re-render
  }
  
  // If hit reference changed, check if it's the same hit by ID
  const prevHitId = prevProps.hit?.objectID || prevProps.hit?.id;
  const nextHitId = nextProps.hit?.objectID || nextProps.hit?.id;
  const hitIdEqual = prevHitId === nextHitId;
  
  // If IDs are different, definitely re-render
  if (!hitIdEqual) {
    return false;
  }
  
  // IDs are the same but reference changed - do shallow comparison of key fields
  // Only re-render if content actually changed
  if (hitRefEqual === false && hitIdEqual) {
    const prevHit = prevProps.hit;
    const nextHit = nextProps.hit;
    
    // Compare key fields that would affect rendering
    // Memoize array joins to avoid creating new strings on every comparison
    const prevLocationStr = prevHit?.location && Array.isArray(prevHit.location) 
      ? prevHit.location.join(',') 
      : String(prevHit?.location || '');
    const nextLocationStr = nextHit?.location && Array.isArray(nextHit.location)
      ? nextHit.location.join(',')
      : String(nextHit?.location || '');
    
    const keyFieldsEqual = 
      prevHit?.title === nextHit?.title &&
      prevHit?.company === nextHit?.company &&
      prevLocationStr === nextLocationStr &&
      prevHit?.salaryRangeMin === nextHit?.salaryRangeMin &&
      prevHit?.salaryRangeMax === nextHit?.salaryRangeMax;
    
    if (keyFieldsEqual && variantEqual && sendEventEqual) {
      return true; // Content is the same, prevent re-render
    }
  }
  
  // Something meaningful changed, allow re-render
  return false;
});

Hit.displayName = 'Hit';
